import React, { useMemo, useRef, useState, useEffect } from "react";
import { Info, X } from "lucide-react";
import { colors, fonts, spacing, radius, TAB_BAR_HEIGHT } from "../theme";
import { ALLERGENS, RESTAURANT_INFO } from "../data/menu";
import { useMenu } from "../hooks/useMenu";

export default function Menu() {
  const { menu } = useMenu();
  const sections = useMemo(() => menu.filter((c) => c.items.length > 0), [menu]);
  const [activeCat, setActiveCat] = useState(sections[0]?.id ?? "");
  const [allergensOpen, setAllergensOpen] = useState(false);
  const sectionRefs = useRef({});
  const chipRefs = useRef({});
  const chipsScrollRef = useRef(null);

  useEffect(() => {
    if (!activeCat && sections[0]) setActiveCat(sections[0].id);
  }, [sections, activeCat]);

  const scrollToCategory = (catId) => {
    setActiveCat(catId);
    const el = sectionRefs.current[catId];
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 118;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
    const chip = chipRefs.current[catId];
    const scroller = chipsScrollRef.current;
    if (chip && scroller) {
      scroller.scrollTo({ left: chip.offsetLeft - scroller.clientWidth / 2 + chip.clientWidth / 2, behavior: "smooth" });
    }
  };

  // Scroll spy
  useEffect(() => {
    const onScroll = () => {
      let current = sections[0]?.id;
      for (const c of sections) {
        const el = sectionRefs.current[c.id];
        if (el && el.getBoundingClientRect().top <= 160) current = c.id;
      }
      if (current && current !== activeCat) {
        setActiveCat(current);
        const chip = chipRefs.current[current];
        const scroller = chipsScrollRef.current;
        if (chip && scroller) {
          scroller.scrollTo({ left: chip.offsetLeft - scroller.clientWidth / 2 + chip.clientWidth / 2, behavior: "smooth" });
        }
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [sections, activeCat]);

  return (
    <div className="pm-fade" style={{ minHeight: "100vh", backgroundColor: colors.surface }}>
      {/* Header */}
      <div style={{ paddingLeft: spacing.xl, paddingRight: spacing.xl, paddingTop: spacing.xl, paddingBottom: spacing.md }}>
        <div style={{ fontFamily: fonts.body, fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: colors.brandPrimary, fontWeight: 500 }}>Il Pescematto</div>
        <div style={{ fontFamily: fonts.display, fontSize: 40, fontWeight: 700, color: colors.onSurface, marginTop: 2, lineHeight: 1.05 }}>Menu</div>
        <div style={{ fontFamily: fonts.body, fontSize: 12, color: colors.muted, marginTop: 4, letterSpacing: 0.4 }}>Coperto {RESTAURANT_INFO.coverCharge}</div>
      </div>

      {/* Sticky chips */}
      <div
        style={{
          position: "sticky", top: 0, zIndex: 20, height: 56, display: "flex", alignItems: "center",
          borderBottom: `0.5px solid ${colors.border}`, backgroundColor: "rgba(5,8,15,0.92)", backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)",
        }}
      >
        <div
          ref={chipsScrollRef}
          style={{ display: "flex", alignItems: "center", gap: spacing.sm, overflowX: "auto", paddingLeft: spacing.xl, paddingRight: spacing.xl, scrollbarWidth: "none" }}
          className="pm-chips"
        >
          {sections.map((c) => {
            const active = c.id === activeCat;
            return (
              <button
                key={c.id}
                ref={(el) => (chipRefs.current[c.id] = el)}
                className="pm-chip"
                onClick={() => scrollToCategory(c.id)}
                style={{
                  height: 36, paddingLeft: spacing.lg, paddingRight: spacing.lg, borderRadius: radius.pill,
                  border: `1px solid ${active ? colors.brandPrimary : colors.border}`,
                  backgroundColor: active ? colors.brandPrimary : "rgba(14,22,36,0.6)",
                  color: active ? colors.onBrandPrimary : colors.onSurfaceSecondary,
                  fontFamily: fonts.body, fontWeight: 500, fontSize: 12, letterSpacing: 0.8, textTransform: "uppercase", whiteSpace: "nowrap", flexShrink: 0,
                }}
              >
                {c.title}
              </button>
            );
          })}
        </div>
      </div>

      {/* Sections */}
      <div style={{ paddingLeft: spacing.xl, paddingRight: spacing.xl, paddingBottom: TAB_BAR_HEIGHT + spacing.xxxl }}>
        {sections.map((section) => (
          <div key={section.id} ref={(el) => (sectionRefs.current[section.id] = el)}>
            {/* Section header */}
            <div style={{ paddingTop: spacing.xxl, paddingBottom: spacing.lg, display: "flex", flexDirection: "column", alignItems: "center" }}>
              <div style={{ display: "flex", alignItems: "center", gap: spacing.md, width: "100%" }}>
                <div style={{ flex: 1, height: 0.5, backgroundColor: colors.brandPrimary, opacity: 0.5 }} />
                <div style={{ fontFamily: fonts.display, fontSize: 22, fontWeight: 700, color: colors.onSurface, letterSpacing: 0.5, textTransform: "uppercase", textAlign: "center" }}>{section.title}</div>
                <div style={{ flex: 1, height: 0.5, backgroundColor: colors.brandPrimary, opacity: 0.5 }} />
              </div>
              {!!section.subtitle && (
                <div style={{ fontFamily: fonts.display, fontStyle: "italic", fontSize: 15, color: colors.muted, marginTop: 6, textAlign: "center" }}>{section.subtitle}</div>
              )}
            </div>

            {/* Items */}
            {section.items.map((item, idx) => (
              <div
                key={`${item.name}-${idx}`}
                style={{
                  paddingTop: spacing.lg, paddingBottom: spacing.lg, display: "flex", alignItems: "flex-start",
                  borderBottom: idx < section.items.length - 1 ? `0.5px solid ${colors.divider}` : "none",
                }}
              >
                <div style={{ flex: 1, paddingRight: spacing.md }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontFamily: fonts.display, fontSize: 19, fontWeight: 700, color: colors.onSurface, letterSpacing: 0.2 }}>{item.name}</span>
                    {!!(item.allergens && item.allergens.length) && (
                      <span style={{ border: `0.5px solid ${colors.borderStrong}`, borderRadius: radius.pill, paddingLeft: 6, paddingRight: 6, paddingTop: 1, paddingBottom: 1, fontFamily: fonts.body, fontSize: 9, letterSpacing: 0.4, color: colors.muted }}>
                        {item.allergens.join(",")}
                      </span>
                    )}
                  </div>
                  {!!item.description && (
                    <div style={{ fontFamily: fonts.body, fontSize: 13, color: colors.muted, marginTop: 4, lineHeight: "18px" }}>{item.description}</div>
                  )}
                </div>
                <div style={{ fontFamily: fonts.display, fontSize: 17, fontWeight: 700, color: colors.brandPrimary, letterSpacing: 0.3, minWidth: 78, textAlign: "right" }}>{item.price}</div>
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* FAB Allergeni */}
      <button
        className="pm-press"
        onClick={() => setAllergensOpen(true)}
        style={{
          position: "fixed", bottom: TAB_BAR_HEIGHT + spacing.md, right: "max(24px, calc(50vw - 236px))",
          display: "flex", alignItems: "center", gap: 6, border: "none", backgroundColor: colors.brandPrimary,
          paddingLeft: spacing.lg, paddingRight: spacing.lg, paddingTop: 12, paddingBottom: 12, borderRadius: radius.pill,
          boxShadow: "0 4px 14px rgba(0,0,0,0.4)", zIndex: 90,
          color: colors.onBrandPrimary, fontFamily: fonts.body, fontWeight: 700, fontSize: 12, letterSpacing: 0.8, textTransform: "uppercase",
        }}
      >
        <Info size={16} color={colors.onBrandPrimary} strokeWidth={2} /> Allergeni
      </button>

      {/* Allergens sheet */}
      {allergensOpen && (
        <div
          className="pm-scrim-anim"
          onClick={() => setAllergensOpen(false)}
          style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.6)", zIndex: 300, display: "flex", justifyContent: "center", alignItems: "flex-end" }}
        >
          <div
            className="pm-sheet-anim"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%", maxWidth: 520, backgroundColor: colors.surfaceSecondary,
              borderTopLeftRadius: radius.lg, borderTopRightRadius: radius.lg,
              paddingLeft: spacing.xl, paddingRight: spacing.xl, paddingTop: spacing.md, maxHeight: "85vh", overflowY: "auto",
            }}
          >
            <div style={{ width: 44, height: 4, borderRadius: radius.pill, backgroundColor: colors.border, margin: "0 auto", marginBottom: spacing.md }} />
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: spacing.md }}>
              <span style={{ fontFamily: fonts.display, fontSize: 26, fontWeight: 700, color: colors.onSurface }}>Allergeni</span>
              <button className="pm-press" onClick={() => setAllergensOpen(false)} style={{ background: "none", border: "none", padding: 0, display: "flex" }}>
                <X size={22} color={colors.onSurface} strokeWidth={2} />
              </button>
            </div>
            <p style={{ fontFamily: fonts.body, fontSize: 13, color: colors.muted, lineHeight: "18px", marginBottom: spacing.lg, marginTop: 0 }}>
              Il ristorante rispetta le normative igienico-sanitarie e informa sulla presenza di allergeni. Comunica al personale eventuali esigenze.
            </p>
            {ALLERGENS.map((a) => (
              <div key={a.id} style={{ display: "flex", alignItems: "flex-start", gap: spacing.md, paddingTop: spacing.sm, paddingBottom: spacing.sm }}>
                <div style={{ width: 28, height: 28, borderRadius: 14, backgroundColor: colors.brandPrimary, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <span style={{ fontFamily: fonts.body, fontWeight: 700, fontSize: 12, color: colors.onBrandPrimary }}>{a.id}</span>
                </div>
                <span style={{ flex: 1, fontFamily: fonts.body, fontSize: 13, color: colors.onSurfaceSecondary, lineHeight: "18px" }}>{a.name}</span>
              </div>
            ))}
            <p style={{ fontFamily: fonts.body, fontStyle: "italic", fontSize: 11, color: colors.muted, marginTop: spacing.lg, marginBottom: spacing.xxl, lineHeight: "16px" }}>
              * Occasionalmente, in mancanza di prodotto fresco, può essere utilizzato un prodotto di pari qualità congelato. Alcuni piatti contengono aglio.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
